import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from './ui/ConfirmDialog';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';

interface AnalyticsData {
  overview: {
    total_users: number;
    active_users: number;
    super_admins: number;
    total_vehicles: number;
    public_vehicles: number;
    total_modifications: number;
    active_modifications: number;
    total_codes: number;
    used_codes: number;
    active_codes: number;
    user_activation_rate: number;
    code_usage_rate: number;
    avg_vehicles_per_user: number;
    avg_modifications_per_vehicle: number;
  };
  users: {
    registration_trend: Array<{ date: string; registrations: number }>;
    demographics: Array<{ country: string; user_count: number }>;
    activity: {
      active_1d: number;
      active_7d: number;
      active_30d: number;
    };
  };
  content: {
    vehicles_trend: Array<{ date: string; vehicles_created: number }>;
    modifications_trend: Array<{ date: string; modifications_created: number }>;
    popular_categories: Array<{ category_id: number; modification_count: number }>;
    top_vehicles: Array<{
      year: string;
      make: string;
      model: string;
      trim: string;
      username: string;
      modification_count: number;
    }>;
  };
  registration_codes: {
    creation_trend: Array<{ date: string; codes_created: number }>;
    usage_trend: Array<{ date: string; codes_used: number }>;
    status_breakdown: Array<{ status: string; count: number }>;
  };
  recent_activity: Array<{
    id: number;
    admin_user_id: number;
    action: string;
    target_type: string;
    target_id: number | null;
    details: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    admin_username: string;
  }>;
  assessment_review_queue?: Array<{
    id: number;
    username: string;
    year: number;
    make: string;
    model: string;
    trim: string;
    base_rarity_stars: number;
    base_collector_points: number;
    assessment_status: string;
    review_reason: string;
  }>;
  import_metrics?: {
    sessions_total: number;
    sessions_completed: number;
    analyze_success_rate: number | null;
    import_completion_rate: number | null;
    rules_vs_ai: { rules: number; ai: number };
  };
  expanded?: Record<string, any>;
  timeframe: string;
  generated_at: string;
}

const StatCard: React.FC<{ label: string; value: string | number | undefined | null }> = ({ label, value }) => (
  <div className="surface rounded-lg p-4">
    <h3 className="text-sm font-medium text-gray-400 mb-1">{label}</h3>
    <p className="text-2xl font-bold text-white">{value ?? '—'}</p>
  </div>
);

export const AdminDashboard: React.FC = () => {
  const { showToast } = useToast();
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedIncludeEvent, setSeedIncludeEvent] = useState(true);
  const [seedIncludeClubs, setSeedIncludeClubs] = useState(true);
  const [seedForce, setSeedForce] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState('30d');
  const [activeSection, setActiveSection] = useState('overview');

  const loadAnalytics = async (tf = timeframe) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminAnalytics(tf);
      setAnalytics(data);
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const handleDataExport = async (type: 'users' | 'codes' | 'analytics') => {
    try {
      let data: any;
      let filename: string;
      
      switch (type) {
        case 'users':
          data = await apiService.getAdminUsers({ limit: 10000 }); // Get all users
          filename = `built-users-${new Date().toISOString().split('T')[0]}.json`;
          break;
        case 'codes':
          data = await apiService.getRegistrationCodes({ limit: 10000 }); // Get all codes
          filename = `built-codes-${new Date().toISOString().split('T')[0]}.json`;
          break;
        case 'analytics':
          data = analytics;
          filename = `built-analytics-${new Date().toISOString().split('T')[0]}.json`;
          break;
        default:
          return;
      }

      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export data:', err);
      showToast(err.message || `Unable to export ${type}.`, 'error');
    }
  };

  const handleSeedDemo = async (dryRun: boolean) => {
    if (!dryRun) {
      setSeedConfirmOpen(true);
      return;
    }
    await runSeedDemo(true);
  };

  const runSeedDemo = async (dryRun: boolean) => {
    setSeedConfirmOpen(false);

    try {
      setSeedLoading(true);
      setSeedError(null);
      setSeedResult(null);
      const data = await apiService.seedDemoShowcase({
        dry_run: dryRun,
        force: seedForce,
        include_event: seedIncludeEvent,
        include_clubs: seedIncludeClubs,
      });
      const lines = [
        data.message || (dryRun ? 'Dry run OK' : 'Seed complete'),
        data.password ? `Password: ${data.password}` : null,
        data.organizer ? `Organizer: @${data.organizer}` : null,
        data.judge ? `Judge: @${data.judge}` : null,
        data.event_slug ? `Event: /events/${data.event_slug}/archive` : null,
        data.discover_hint ? `Discover: ${data.discover_hint}` : null,
        typeof data.vehicles_upserted === 'number'
          ? `Vehicles: ${data.vehicles_upserted} (${data.vehicles_created ?? 0} new)`
          : null,
        typeof data.modifications === 'number' ? `Parts: ${data.modifications}` : null,
        typeof data.images === 'number' ? `Photos: ${data.images}` : null,
        typeof data.badge_definitions === 'number' || typeof data.badges_issued === 'number'
          ? `Badges: ${data.badge_definitions ?? 0} defs, ${data.badges_issued ?? 0} issued`
          : null,
        ...(data.clubs || []).map(
          (c) => `Club: ${c.path} · ${c.name} · ${c.verification_status}`
        ),
        ...(data.builds || []).slice(0, 12).map(
          (b) => `${b.path} · ${b.mods} parts · ${b.public ? 'public' : 'private'}`
        ),
      ].filter(Boolean);
      setSeedResult(lines.join('\n'));
      if (!dryRun) {
        await loadAnalytics();
      }
    } catch (err: any) {
      setSeedError(err.message || 'Seed failed');
    } finally {
      setSeedLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-red-400 font-medium">Failed to load analytics</h3>
            <p className="text-gray-300 text-sm mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={() => void loadAnalytics()}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center text-gray-400">
        <p>No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={seedConfirmOpen}
        title="Seed showcase demo data?"
        description={
          seedForce
            ? 'Force-seed on this environment. Existing demo_* accounts will be refreshed.'
            : 'Seeds demo users, cars, nested builds, photos, badges/clubs, and optionally a sample event. Scoped to demo_* accounts.'
        }
        confirmLabel="Seed demo data"
        cancelLabel="Cancel"
        loading={seedLoading}
        loadingLabel="Seeding…"
        onCancel={() => setSeedConfirmOpen(false)}
        onConfirm={() => runSeedDemo(false)}
      />
      <PageHeader
        eyebrow="Admin"
        title="Analytics"
        description="Platform management and analytics."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="analytics-timeframe" className="sr-only">
              Date range
            </label>
            <select
              id="analytics-timeframe"
              value={timeframe}
              onChange={(e) => {
                setTimeframe(e.target.value);
                void loadAnalytics(e.target.value);
              }}
              className="input min-h-[44px] text-sm"
            >
              <option value="today">Today</option>
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
              <option value="1y">Year</option>
              <option value="all">All time</option>
            </select>
            <Button type="button" variant="secondary" onClick={() => void loadAnalytics()} className="min-h-[44px]">
              Refresh
            </Button>
          </div>
        }
      />

      {analytics.expanded && (
        <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
          <h2 className="text-xl font-semibold text-white mb-4">Platform analytics ({analytics.timeframe})</h2>
          <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Analytics sections">
            {['overview', 'shops', 'engagement', 'community', 'events', 'clubs', 'media', 'achievements', 'search', 'operational'].map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeSection === key}
                onClick={() => setActiveSection(key)}
                className={`px-3 py-1.5 rounded-lg text-sm min-h-[36px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
                  activeSection === key ? 'bg-orange-500/15 text-orange-300 border border-orange-500/35' : 'text-gray-400 border border-white/[0.08] hover:bg-white/[0.06]'
                }`}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>

          {activeSection === 'overview' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="New users" value={analytics.expanded.platform_growth?.new_users} />
              <StatCard label="New vehicles" value={analytics.expanded.platform_growth?.new_vehicles} />
              <StatCard label="New mods" value={analytics.expanded.platform_growth?.new_modifications} />
              <StatCard label="DAU / WAU / MAU" value={`${analytics.expanded.engagement?.dau ?? 0} / ${analytics.expanded.engagement?.wau ?? 0} / ${analytics.expanded.engagement?.mau ?? 0}`} />
            </div>
          )}

          {activeSection === 'shops' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Approved shops" value={analytics.expanded.shops?.shops?.approved} />
              <StatCard label="Pending review" value={analytics.expanded.shops?.shops?.pending_review} />
              <StatCard label="Shops w/ builds" value={analytics.expanded.shops?.shops_with_builds} />
              <StatCard label="Approval rate" value={analytics.expanded.shops?.approval_rate_pct != null ? `${analytics.expanded.shops.approval_rate_pct}%` : '—'} />
              <StatCard label="Avg approval (hrs)" value={analytics.expanded.shops?.avg_approval_hours ?? '—'} />
              <StatCard label="Merge rate" value={analytics.expanded.shops?.merge_rate_pct != null ? `${analytics.expanded.shops.merge_rate_pct}%` : '—'} />
            </div>
          )}

          {activeSection === 'engagement' && (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="DAU" value={analytics.expanded.engagement?.dau} />
              <StatCard label="WAU" value={analytics.expanded.engagement?.wau} />
              <StatCard label="MAU" value={analytics.expanded.engagement?.mau} />
            </div>
          )}

          {activeSection === 'community' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Approved contributors" value={analytics.expanded.community?.approved_contributors} />
              <StatCard label="New contributors" value={analytics.expanded.community?.new_contributors} />
              <StatCard label="Pending" value={analytics.expanded.community?.pending_contributors} />
            </div>
          )}

          {activeSection === 'events' && (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Total events" value={analytics.expanded.events?.total_events} />
              <StatCard label="New events" value={analytics.expanded.events?.new_events} />
              <StatCard label="Registrations" value={analytics.expanded.events?.total_registrations} />
            </div>
          )}

          {activeSection === 'clubs' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Clubs" value={analytics.expanded.clubs?.total_clubs} />
              <StatCard label="Verified" value={analytics.expanded.clubs?.verified_clubs} />
              <StatCard label="New clubs" value={analytics.expanded.clubs?.new_clubs} />
              <StatCard label="Active members" value={analytics.expanded.clubs?.active_memberships} />
            </div>
          )}

          {activeSection === 'media' && (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Active images" value={analytics.expanded.media?.active_images} />
              <StatCard label="New images" value={analytics.expanded.media?.new_images} />
              <StatCard label="Albums" value={analytics.expanded.media?.albums} />
            </div>
          )}

          {activeSection === 'achievements' && (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Definitions" value={analytics.expanded.achievements?.definitions} />
              <StatCard label="Current issued" value={analytics.expanded.achievements?.current_issued} />
              <StatCard label="Newly issued" value={analytics.expanded.achievements?.newly_issued} />
            </div>
          )}

          {activeSection === 'search' && (
            <StatCard label="Search events" value={analytics.expanded.search?.search_events} />
          )}

          {activeSection === 'operational' && (
            <div className="grid grid-cols-2 gap-4">
              <StatCard label="Admin actions" value={analytics.expanded.operational?.admin_actions} />
              <StatCard label="Pending certifications" value={analytics.expanded.operational?.pending_certifications} />
            </div>
          )}

          {analytics.expanded.top_lists?.top_shops_by_builds?.length > 0 && activeSection === 'shops' && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-300 mb-2">Top shops by credited builds</h3>
              <ul className="space-y-1 list-none text-sm">
                {analytics.expanded.top_lists.top_shops_by_builds.slice(0, 8).map((s: { name: string; slug: string; build_count: number }) => (
                  <li key={s.slug} className="flex justify-between text-gray-400">
                    <Link to={`/shops/${s.slug}`} className="text-orange-300 hover:underline">
                      {s.name}
                    </Link>
                    <span>{s.build_count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Platform Overview */}
      <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Platform Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="surface rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total Users</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-white">{formatNumber(analytics.overview.total_users)}</p>
              <span className="ml-2 text-sm text-green-400">
                ({analytics.overview.active_users} active)
              </span>
            </div>
          </div>
          <div className="surface rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total Vehicles</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-white">{formatNumber(analytics.overview.total_vehicles)}</p>
              <span className="ml-2 text-sm text-green-400">
                ({analytics.overview.public_vehicles} public)
              </span>
            </div>
          </div>
          <div className="surface rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total Modifications</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-white">{formatNumber(analytics.overview.total_modifications)}</p>
              <span className="ml-2 text-sm text-green-400">
                ({analytics.overview.active_modifications} active)
              </span>
            </div>
          </div>
          <div className="surface rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Registration Codes</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-white">
                {analytics.overview.active_codes}/{analytics.overview.total_codes}
              </p>
              <span className="ml-2 text-sm text-gray-400">Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Collectibility review queue (S3-18) */}
      {analytics.assessment_review_queue && analytics.assessment_review_queue.length > 0 && (
        <div className="rounded-lg p-6 border border-amber-800/50 bg-[#0c0c0c]">
          <h2 className="text-xl font-semibold text-white mb-2 flex items-center">
            <span className="text-amber-400 mr-2">⚠</span>
            Collectibility Review Queue
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Vehicles with failed assessments or extreme / low-confidence scores
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-gray-400 border-b border-white/[0.06]">
                  <th className="py-2 pr-4">Vehicle</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">Score</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {analytics.assessment_review_queue.slice(0, 10).map((item) => (
                  <tr key={item.id} className="border-b border-white/[0.06]/50 text-gray-300">
                    <td className="py-2 pr-4">
                      {item.year} {item.make} {item.model} {item.trim}
                    </td>
                    <td className="py-2 pr-4">{item.username || '-'}</td>
                    <td className="py-2 pr-4">
                      {item.base_rarity_stars}★ / {item.base_collector_points} pts
                    </td>
                    <td className="py-2 pr-4 text-amber-300">{item.review_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Smart Paste import metrics (S4-12) */}
      {analytics.import_metrics && analytics.import_metrics.sessions_total > 0 && (
        <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
          <h2 className="text-xl font-semibold text-white mb-4">Smart Paste Import (30d)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="surface rounded-lg p-3">
              <div className="text-gray-400 text-xs">Sessions</div>
              <div className="text-white font-semibold">{analytics.import_metrics.sessions_total}</div>
            </div>
            <div className="surface rounded-lg p-3">
              <div className="text-gray-400 text-xs">Completed</div>
              <div className="text-white font-semibold">{analytics.import_metrics.sessions_completed}</div>
            </div>
            <div className="surface rounded-lg p-3">
              <div className="text-gray-400 text-xs">Rules vs AI</div>
              <div className="text-white font-semibold text-sm">
                {analytics.import_metrics.rules_vs_ai.rules} / {analytics.import_metrics.rules_vs_ai.ai}
              </div>
            </div>
            <div className="surface rounded-lg p-3">
              <div className="text-gray-400 text-xs">Avg completion</div>
              <div className="text-white font-semibold">
                {analytics.import_metrics.import_completion_rate != null
                  ? `${Math.round(analytics.import_metrics.import_completion_rate * 100)}%`
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Statistics */}
      <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2v2m0 0V9a2 2 0 012-2h14a2 2 0 012 2v2M7 7V5a2 2 0 012-2h6a2 2 0 012 2v2" />
          </svg>
          Content Statistics
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="surface rounded-lg p-3 min-w-0">
              <div className="text-gray-400 text-sm mb-1">Total Vehicles</div>
              <div className="text-white font-semibold text-lg">
                {formatNumber(analytics.overview.total_vehicles)}
              </div>
              <div className="text-green-400 text-xs">
                ({analytics.overview.public_vehicles} public)
              </div>
            </div>
            <div className="surface rounded-lg p-3 min-w-0">
              <div className="text-gray-400 text-sm mb-1">Total Modifications</div>
              <div className="text-white font-semibold text-lg">
                {formatNumber(analytics.overview.total_modifications)}
              </div>
              <div className="text-green-400 text-xs">
                ({analytics.overview.active_modifications} active)
              </div>
            </div>
            <div className="surface rounded-lg p-3 min-w-0">
              <div className="text-gray-400 text-sm mb-1">Avg Mods/Vehicle</div>
              <div className="text-white font-semibold text-lg">
                {analytics.overview.avg_modifications_per_vehicle}
              </div>
              <div className="text-orange-400 text-xs">
                (ratio)
              </div>
            </div>
            <div className="surface rounded-lg p-3 min-w-0">
              <div className="text-gray-400 text-sm mb-1">Most Active Category</div>
              <div className="text-orange-400 font-semibold text-sm truncate">
                {analytics.content.popular_categories.length > 0 ? analytics.content.popular_categories[0].category_id.toString() : 'N/A'}
              </div>
              <div className="text-gray-400 text-xs">
                ({analytics.content.popular_categories.length > 0 ? analytics.content.popular_categories[0].modification_count.toString() : 'N/A'} mods)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Link
            to="/admin/codes"
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
          >
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="truncate">Generate Invite Code</span>
          </Link>
          <Link
            to="/admin/users"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
          >
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <span className="truncate">Manage Users</span>
          </Link>
          <a
            href="#data-export"
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
          >
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="truncate">Export Data</span>
          </a>
          <a
            href="#demo-seed"
            className="surface hover:bg-white/[0.06] text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center text-sm font-medium sm:col-span-2 lg:col-span-1"
          >
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <span className="truncate">Seed demo data</span>
          </a>
        </div>
      </div>

      {/* Recent Activity */}
      {analytics.recent_activity && analytics.recent_activity.length > 0 && (
        <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Recent Admin Activity
          </h2>
          <div className="space-y-3">
            {analytics.recent_activity.slice(0, 5).map((activity) => (
              <div key={activity.id} className="flex items-center justify-between surface rounded-lg p-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-white text-sm">
                      <span className="font-medium">{activity.admin_username}</span>
                      <span className="text-gray-400 ml-1">{activity.action}</span>
                      <span className="text-orange-400 ml-1">{activity.target_type}</span>
                    </p>
                  </div>
                </div>
                <span className="text-gray-400 text-xs">
                  {new Date(activity.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Activity Charts */}
      {analytics.users && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Registration Trends Chart */}
          <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Registration Trends
            </h2>
            <div className="space-y-3">
              {analytics.users.registration_trend.slice(0, 6).map((item, index) => {
                const maxCount = Math.max(...analytics.users.registration_trend.map(i => i.registrations));
                const percentage = (item.registrations / maxCount) * 100;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <span className="text-sm text-gray-400 w-20">{item.date}</span>
                      <div className="flex-1 mx-3">
                        <div className="bg-white/[0.06] rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-orange-500 to-orange-800 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-white font-medium w-8">{item.registrations}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* User Demographics Chart */}
          <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              User Distribution
            </h2>
            <div className="space-y-3">
              {analytics.users.demographics.slice(0, 6).map((item, index) => {
                const maxCount = Math.max(...analytics.users.demographics.map(i => i.user_count));
                const percentage = (item.user_count / maxCount) * 100;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <span className="text-sm text-gray-400 w-20 truncate">{item.country}</span>
                      <div className="flex-1 mx-3">
                        <div className="bg-white/[0.06] rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-orange-500 to-orange-800 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-white font-medium w-8">{item.user_count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Demo seed */}
      <div id="demo-seed" className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <h2 className="text-xl font-semibold text-white mb-2 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          Showcase demo data
        </h2>
        <p className="text-gray-400 text-sm mb-4 max-w-3xl">
          Populate realistic example users, cars, photos, nested parent/child parts, edge-case builds
          (empty garage, dense list, private secondary car), discovery-friendly identities, recognition
          badges, clubs (verified + unverified), plus an optional completed event with awards.
          Safe to re-run. Only touches <span className="text-gray-300">demo_*</span> accounts.
          Requires discovery/badge/club migrations.
        </p>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center mb-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer min-h-[40px]">
            <input
              type="checkbox"
              checked={seedIncludeEvent}
              onChange={(e) => setSeedIncludeEvent(e.target.checked)}
              className="rounded border-white/[0.10] bg-[#0a0a0a] text-orange-500 focus:ring-orange-500"
            />
            Include sample event &amp; awards
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer min-h-[40px]">
            <input
              type="checkbox"
              checked={seedIncludeClubs}
              onChange={(e) => setSeedIncludeClubs(e.target.checked)}
              className="rounded border-white/[0.10] bg-[#0a0a0a] text-orange-500 focus:ring-orange-500"
            />
            Include clubs &amp; club badges
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer min-h-[40px]">
            <input
              type="checkbox"
              checked={seedForce}
              onChange={(e) => setSeedForce(e.target.checked)}
              className="rounded border-white/[0.10] bg-[#0a0a0a] text-orange-500 focus:ring-orange-500"
            />
            Allow on production (<span className="text-meta">force</span>)
          </label>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            disabled={seedLoading}
            onClick={() => handleSeedDemo(true)}
            className="btn-secondary btn-sm disabled:opacity-40"
          >
            Preview (dry run)
          </button>
          <button
            type="button"
            disabled={seedLoading}
            onClick={() => handleSeedDemo(false)}
            className="btn-primary btn-sm disabled:opacity-40"
          >
            {seedLoading ? 'Seeding…' : 'Seed showcase data'}
          </button>
        </div>
        {seedError && (
          <div className="alert-error mb-3" role="alert">
            {seedError}
          </div>
        )}
        {seedResult && (
          <pre className="text-xs text-gray-300 bg-[#0a0a0a]/60 border border-white/[0.07] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
            {seedResult}
          </pre>
        )}
      </div>

      {/* Data Export Section */}
      <div id="data-export" className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="truncate">Data Export</span>
        </h2>
        <p className="text-gray-400 mb-4 text-sm">Export platform data for analysis and reporting</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button 
            onClick={() => handleDataExport('users')}
            className="flex items-center justify-center p-3 bg-blue-600/20 border border-blue-600/30 rounded-lg hover:bg-blue-600/30 transition-colors min-w-0"
          >
            <svg className="w-4 h-4 mr-2 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <span className="text-blue-400 font-medium text-sm truncate">Export Users</span>
          </button>
          <button 
            onClick={() => handleDataExport('codes')}
            className="flex items-center justify-center p-3 bg-orange-600/20 border border-orange-600/30 rounded-lg hover:bg-orange-600/30 transition-colors min-w-0"
          >
            <svg className="w-4 h-4 mr-2 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <span className="text-orange-400 font-medium text-sm truncate">Export Codes</span>
          </button>
          <button 
            onClick={() => handleDataExport('analytics')}
            className="flex items-center justify-center p-3 bg-green-600/20 border border-green-600/30 rounded-lg hover:bg-green-600/30 transition-colors min-w-0 sm:col-span-2 lg:col-span-1"
          >
            <svg className="w-4 h-4 mr-2 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-green-400 font-medium text-sm truncate">Export Analytics</span>
          </button>
        </div>
      </div>
    </div>
  );
}; 